export type AccountStatus = {
  connected: boolean;
  configured: boolean;
  displayName?: string;
  destinationPath: string | null;
  entitled: boolean;
  remainingUploads: number;
  billingConfigured: boolean;
  canManageBilling: boolean;
};

export type DropboxFolder = { name: string; path: string };

type ApiErrorBody = {
  error?: string;
  checkoutRequired?: boolean;
  quotaExceeded?: 'count' | 'bytes';
  resetAt?: number;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly checkoutRequired = false,
    readonly quotaExceeded?: 'count' | 'bytes',
    readonly resetAt?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const API_ORIGIN = 'https://receipt.bucksimple.com';

export function apiUrl(path: string): string {
  return new URL(path, API_ORIGIN).toString();
}

export function dropboxStartUrl(): string {
  const returnTo = `${window.location.origin}${window.location.pathname}`;
  return `${apiUrl('/api/dropbox/start')}?returnTo=${encodeURIComponent(returnTo)}`;
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  let body: (T & ApiErrorBody) | undefined;
  try {
    body = (await response.json()) as T & ApiErrorBody;
  } catch {
    if (!response.ok) throw new ApiError(fallback, response.status);
    throw new ApiError('The BuckSimple server returned an unreadable response.', response.status);
  }
  if (!response.ok) {
    throw new ApiError(
      body.error || fallback,
      response.status,
      Boolean(body.checkoutRequired),
      body.quotaExceeded,
      body.resetAt,
    );
  }
  return body;
}

export async function getStatus(signal?: AbortSignal): Promise<AccountStatus> {
  const response = await fetch(apiUrl('/api/status'), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal,
  });
  return readJson<AccountStatus>(response, 'Dropbox connection status is unavailable.');
}

export async function getFolders(signal?: AbortSignal): Promise<{
  folders: DropboxFolder[];
  destinationPath: string | null;
}> {
  const response = await fetch(apiUrl('/api/dropbox/folders'), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal,
  });
  return readJson(response, 'Dropbox folders are unavailable.');
}

export async function chooseFolder(input: { action: 'select'; path: string } | { action: 'create'; name: string }): Promise<{
  destinationPath: string;
}> {
  const response = await fetch(apiUrl('/api/dropbox/folders'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson(response, 'The folder could not be saved.');
}

export async function disconnectDropbox(): Promise<void> {
  const response = await fetch(apiUrl('/api/dropbox/disconnect'), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  await readJson<Record<string, unknown>>(response, 'Dropbox could not be disconnected.');
}

export async function beginBilling(kind: 'checkout' | 'portal'): Promise<string> {
  const response = await fetch(apiUrl(`/api/billing/${kind}`), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const result = await readJson<{ url?: string }>(response, 'Billing is unavailable.');
  if (!result.url) throw new ApiError('Billing is unavailable.', response.status);
  return result.url;
}

export type UploadResult = { path: string; remainingUploads?: number; successfulUploads?: number };

export function uploadDocument(
  file: File,
  targetName: string,
  onProgress: (percent: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', apiUrl('/api/upload'));
    request.withCredentials = true;
    request.responseType = 'json';
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    request.setRequestHeader('X-Document-Filename', encodeURIComponent(targetName));
    request.setRequestHeader('Accept', 'application/json');
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
    });
    request.addEventListener('load', () => {
      const result = (request.response || {}) as {
        path?: string;
        error?: string;
        remainingUploads?: number;
        successfulUploads?: number;
        checkoutRequired?: boolean;
        quotaExceeded?: 'count' | 'bytes';
        resetAt?: number;
      };
      if (request.status >= 200 && request.status < 300 && result.path) {
        onProgress(100);
        resolve({ path: result.path, remainingUploads: result.remainingUploads, successfulUploads: result.successfulUploads });
        return;
      }
      reject(new ApiError(
        result.error || 'Dropbox could not save that document.',
        request.status,
        Boolean(result.checkoutRequired),
        result.quotaExceeded,
        result.resetAt,
      ));
    });
    request.addEventListener('error', () => reject(new ApiError('The connection stopped. Try this file again.', 0)));
    request.addEventListener('abort', () => reject(new ApiError('The upload was cancelled.', 0)));
    request.send(file);
  });
}
